import { filter, flatMap, fromPairs, includes, intersection, isEmpty, isNull, keys, map } from 'es-toolkit/compat';
import { useMemo } from 'react';

import { AnnotationAssignment, AnnotationWorkingStates } from 'interfaces/annotation';
import { ClassConfig, ClassConfigYamlSchema } from 'interfaces/model';

export const filterAnnotationAssignments = ({
  annotationAssignments,
  selectedAssignmentIds,
  selectedSlideIds,
  workingStatesToUse,
}: {
  annotationAssignments: AnnotationAssignment[];
  selectedAssignmentIds: number[];
  selectedSlideIds?: string[];
  workingStatesToUse?: AnnotationWorkingStates[];
}) =>
  map(
    filter(
      annotationAssignments,
      (assignment) =>
        includes(selectedAssignmentIds, assignment?.annotationAssignmentId) &&
        (!selectedSlideIds || !isEmpty(intersection(selectedSlideIds, assignment?.slideIds)))
    ),
    (assignment) => ({
      ...assignment,
      annotations: filter(
        assignment?.annotations,
        (annotation) =>
          (!selectedSlideIds || includes(selectedSlideIds, annotation?.slideId)) &&
          (!workingStatesToUse || includes(workingStatesToUse, annotation?.workingState))
      ),
    })
  );

export const useFilterAndFlattenAnnotationAssignments = ({
  annotationAssignments,
  selectedAssignmentIds,
  selectedSlideIds,
  workingStatesToUse,
}: {
  annotationAssignments: AnnotationAssignment[];
  selectedAssignmentIds: number[];
  selectedSlideIds?: string[];
  workingStatesToUse?: AnnotationWorkingStates[];
}) => {
  return useMemo(() => {
    const selectedAnnotationAssignments = filterAnnotationAssignments({
      annotationAssignments,
      selectedAssignmentIds,
      selectedSlideIds,
      workingStatesToUse,
    });
    return flatMap(selectedAnnotationAssignments, (assignment) =>
      flatMap(assignment?.todos, (todo) => ({ ...assignment, ...todo }))
    );
  }, [workingStatesToUse, annotationAssignments, selectedAssignmentIds, selectedSlideIds]);
};

export const generateClassConfigFromForm = ({
  classesConfigFromModel,
  trainingClassToAnnotationOptions,
  disabledTrainingTargets,
}: {
  classesConfigFromModel: Record<string, ClassConfig>;
  trainingClassToAnnotationOptions: Record<string, string[]>;
  disabledTrainingTargets: string[];
}): Record<string, ClassConfigYamlSchema> => {
  const trainingClassesToUse = filter(
    keys(classesConfigFromModel),
    (className) => !includes(disabledTrainingTargets, className)
  );
  return fromPairs(
    map(trainingClassesToUse, (className, id) => [
      className,
      {
        data_items: trainingClassToAnnotationOptions[className] || [],
        id,
        ...(!isNull(classesConfigFromModel?.[className]?.color)
          ? { color: classesConfigFromModel?.[className]?.color }
          : {}),
        ...(!isNull(classesConfigFromModel?.[className]?.ignore)
          ? { ignore: classesConfigFromModel?.[className]?.ignore }
          : {}),
      },
    ])
  );
};
